/**
 * founsiSlice — Redux state for the Founsi AI chat tab.
 *
 * Owns:
 *  - The current thread's message tree (user + assistant bubbles +
 *    inline tool-call/result event log).
 *  - The mid-stream state machine (`status`: idle | submitting |
 *    streaming | done | error), tracked separately from the per-turn
 *    `done.content` so the FE never blocks rendering on a final
 *    metadata-rich payload it doesn't need.
 *  - A simple `Map<call_id, tool_name>` correlation so the BE's
 *    `tool_result` events (which carry no `tool` field) can be
 *    rendered with the originating tool's name.  See the audit note
 *    on frontend#259.
 *
 * Threads list + history replay (#261) live in a separate slice;
 * this slice is the one-thread-in-flight surface only.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AssistantSseEvent } from './api/streamAssistantChat';
import type { ThreadMessage } from './api/threadsApi';

/** Shape of a tool result, mirroring the BE wire (status + data +
 *  optional error_code). */
export interface ToolResult {
  status: string;
  data: unknown;
  error_code: string | null;
}

export type ChatStatus =
  | 'idle'
  | 'submitting' // request opened, no `thread`/`thinking` yet
  | 'thinking' // `thinking` seen, no `content` tokens yet
  | 'streaming' // `content` tokens flowing in
  | 'done'
  | 'error';

export interface UserBubble {
  kind: 'user';
  /** Local-only id (we don't have the server's `assistant_message.id`
   *  until the next `GET /threads/{id}/messages`). */
  localId: string;
  text: string;
  /** Optional thumbnails the user attached. */
  attachments?: Array<{ type: 'image'; file_id: string; preview?: string }>;
  /** ISO timestamp set at dispatch time. */
  timestamp: string;
}

export interface AssistantBubble {
  kind: 'assistant';
  localId: string;
  /** Accumulated `content` tokens (the streaming text body). */
  text: string;
  /** True after a `done` event lands.  Stops the spinner. */
  complete: boolean;
  /** True if the BE emitted an `error` event for this bubble. */
  errored: boolean;
  /** Set from `done.content` so we can render the authoritative
   *  answer even if our token concat lost a chunk on disconnect. */
  finalContent?: string;
  /** Surfaced from terminal `error` event so the FE can offer a
   *  retry CTA. */
  errorCode?: string;
  errorMessage?: string;
  errorRetryable?: boolean;
  /** Inline progress log: ordered tool events as they came in.
   *  Each call_id has its tool name set by the matching `tool_call`
   *  and its result set by the matching `tool_result`.  The Map
   *  semantics live in the slice (see `toolCalls` below) — the
   *  array here just preserves emission order. */
  toolEvents: Array<{
    call_id: string;
    /** Resolved at tool_call time and reused for tool_result. */
    tool: string;
    args?: Record<string, unknown>;
    result?: ToolResult;
  }>;
  /** Per-message metadata from the `done` event.  Useful for a
   *  debug/usage chip. */
  metadata?: {
    model: string;
    provider: string;
    tokens_in: number;
    tokens_out: number;
    cost_usd: number;
    latency_ms: number;
    iterations: number;
  };
}

export type Bubble = UserBubble | AssistantBubble;

export interface FounsiState {
  /** Current thread id; null = a new thread will be created on
   *  the next `submit`.  Set from the first `thread` SSE event. */
  threadId: string | null;
  /** Auto-generated title from the BE — surfaced in the header. */
  threadTitle: string | null;
  bubbles: Bubble[];
  status: ChatStatus;
  /** Maps SSE `call_id` → tool name so `tool_result` events (no
   *  `tool` field per the BE wire) can be displayed with the
   *  originating tool's name.  Reset between turns. */
  toolNameByCallId: Record<string, string>;
}

const initialState: FounsiState = {
  threadId: null,
  threadTitle: null,
  bubbles: [],
  status: 'idle',
  toolNameByCallId: {},
};

const founsiSlice = createSlice({
  name: 'founsi',
  initialState,
  reducers: {
    /** Open a brand-new thread.  Wipes local state. */
    startNewThread(state) {
      state.threadId = null;
      state.threadTitle = null;
      state.bubbles = [];
      state.status = 'idle';
      state.toolNameByCallId = {};
    },
    /** Optimistically append the user's bubble before the stream
     *  starts.  Per the BE contract, the user_message is persisted
     *  on the server BEFORE the stream begins, so showing the bubble
     *  immediately is safe — a disconnect mid-stream won't make this
     *  bubble lie about what was sent. */
    appendUserBubble(state, action: PayloadAction<UserBubble>) {
      state.bubbles.push(action.payload);
      state.status = 'submitting';
    },
    /** Mount a placeholder assistant bubble that subsequent SSE
     *  events fill in.  Called right after `appendUserBubble`. */
    appendAssistantPlaceholder(
      state,
      action: PayloadAction<{ localId: string }>,
    ) {
      state.bubbles.push({
        kind: 'assistant',
        localId: action.payload.localId,
        text: '',
        complete: false,
        errored: false,
        toolEvents: [],
      });
      state.toolNameByCallId = {};
    },
    /** Apply an SSE event to the last assistant bubble. */
    applySseEvent(state, action: PayloadAction<AssistantSseEvent>) {
      const ev = action.payload;
      const last = state.bubbles[state.bubbles.length - 1];
      if (!last || last.kind !== 'assistant') return;

      switch (ev.event) {
        case 'thread':
          state.threadId = ev.thread_id;
          state.threadTitle = ev.title;
          break;
        case 'thinking':
          state.status = 'thinking';
          break;
        case 'tool_call':
          state.toolNameByCallId[ev.call_id] = ev.tool;
          last.toolEvents.push({
            call_id: ev.call_id,
            tool: ev.tool,
            args: ev.args,
          });
          break;
        case 'tool_result': {
          // Reconcile against the prior tool_call by call_id (the
          // BE wire doesn't carry `tool` on tool_result — this is
          // the call-out from the audit on #259).
          const toolName = state.toolNameByCallId[ev.call_id];
          const existing = last.toolEvents.find(
            (t) => t.call_id === ev.call_id,
          );
          if (existing) {
            existing.result = ev.result;
            // Defensive: if we somehow missed the tool_call event,
            // backfill the tool name from the correlation map.
            if (toolName && !existing.tool) existing.tool = toolName;
          } else {
            last.toolEvents.push({
              call_id: ev.call_id,
              tool: toolName ?? 'unknown',
              result: ev.result,
            });
          }
          break;
        }
        case 'content':
          last.text += ev.token;
          state.status = 'streaming';
          break;
        case 'done':
          last.text = ev.content; // BE-authoritative final answer
          last.finalContent = ev.content;
          last.complete = true;
          last.metadata = {
            model: ev.model,
            provider: ev.provider,
            tokens_in: ev.tokens_in,
            tokens_out: ev.tokens_out,
            cost_usd: ev.cost_usd,
            latency_ms: ev.latency_ms,
            iterations: ev.iterations,
          };
          state.status = 'done';
          break;
        case 'error':
          last.errored = true;
          last.complete = true;
          last.errorCode = ev.code;
          last.errorMessage = ev.message;
          last.errorRetryable = ev.retryable;
          state.status = 'error';
          break;
      }
    },
    /** Pre-stream HTTP error (e.g. 429 rate-limit, 422 validation).
     *  The bubble was never streamed into; we mark it errored so
     *  the UI can show the same error treatment. */
    setPreStreamError(
      state,
      action: PayloadAction<{
        code: string | null;
        message: string;
        status: number;
      }>,
    ) {
      const last = state.bubbles[state.bubbles.length - 1];
      if (last && last.kind === 'assistant') {
        last.errored = true;
        last.complete = true;
        last.errorCode = action.payload.code ?? `http_${action.payload.status}`;
        last.errorMessage = action.payload.message;
        // 429s are retryable by definition (just wait); validation
        // errors aren't.
        last.errorRetryable = action.payload.status === 429;
      }
      state.status = 'error';
    },
    /** User tapped stop / navigated away. */
    abortStream(state) {
      const last = state.bubbles[state.bubbles.length - 1];
      if (last && last.kind === 'assistant' && !last.complete) {
        last.complete = true; // freeze visual state; keep the partial
      }
      state.status = 'idle';
    },
    /** Resume an existing thread (clear local state, set thread id). */
    resumeThread(
      state,
      action: PayloadAction<{ threadId: string; title?: string }>,
    ) {
      state.threadId = action.payload.threadId;
      state.threadTitle = action.payload.title ?? null;
      state.bubbles = [];
      state.status = 'idle';
      state.toolNameByCallId = {};
    },
    /** Replay a thread loaded from `GET /threads/{id}/messages`.
     *
     *  The BE persists only the final user/assistant text exchange
     *  to message history — transient `tool_call`/`tool_result`
     *  streaming events are NOT persisted, so a replayed assistant
     *  bubble has an empty `toolEvents[]`.  This matches what
     *  ChatGPT/Claude do (they don't replay tool steps either),
     *  and is the reason replayed bubbles render the final answer
     *  text directly without the tool-chip strip.
     *
     *  `content` is a multi-part array; we flatten any `text` parts
     *  into a single string and ignore unknown part types
     *  (forward-compat with future BE multi-modal additions). */
    replayThread(
      state,
      action: PayloadAction<{
        threadId: string;
        title?: string;
        messages: ThreadMessage[];
      }>,
    ) {
      state.threadId = action.payload.threadId;
      state.threadTitle = action.payload.title ?? null;
      state.toolNameByCallId = {};
      state.status = 'idle';
      // BE returns messages descending by created_at (newest first)
      // for pagination cursoring.  The chat surface renders oldest
      // at top → newest at bottom, so flip into chronological
      // order before mapping.  Sorting (not just reversing) is
      // robust against future BE re-ordering and tied timestamps.
      const ordered = [...action.payload.messages].sort((a, b) =>
        a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0,
      );
      state.bubbles = ordered.map((m) => {
        const text = m.content
          .filter((p) => p.type === 'text' && typeof p.text === 'string')
          .map((p) => p.text!)
          .join('');
        if (m.role === 'user') {
          return {
            kind: 'user',
            localId: m.id,
            text,
            timestamp: m.created_at,
          };
        }
        // assistant
        return {
          kind: 'assistant',
          localId: m.id,
          text,
          complete: true,
          errored: false,
          finalContent: text,
          toolEvents: [],
          metadata:
            m.model_id && m.provider
              ? {
                  model: m.model_id,
                  provider: m.provider,
                  tokens_in: m.tokens_in ?? 0,
                  tokens_out: m.tokens_out ?? 0,
                  cost_usd: m.cost_usd ?? 0,
                  latency_ms: m.latency_ms ?? 0,
                  iterations: 0, // not persisted on message history
                }
              : undefined,
        };
      });
    },
  },
});

export const {
  startNewThread,
  appendUserBubble,
  appendAssistantPlaceholder,
  applySseEvent,
  setPreStreamError,
  abortStream,
  resumeThread,
  replayThread,
} = founsiSlice.actions;

export default founsiSlice.reducer;
